import {
  BloomLevelEnum,
  CBQQuesContentModel,
  FibContentModel,
  LongQuesContentModel,
  McqMultipleContentModel,
  McqSingleContentModel,
  OptionsType,
  Question,
  Question_QuestionEnum,
  ResourceCategoryEnum,
  ShortQuesContentModel,
  TfContentModel,
  TfContentModel_AnswerEnum,
  VeryShortQuesContentModel,
} from '@protos/content_management/content.db_pb';
import { QAttemptResultEnum } from '@protos/learning_management/lms.db_pb';
import { getMediaBasePath } from '../../../commonUtils/images';
import ImageWrapper from '../../elements/ImageWrapper';
import { Fib } from './QuestionTypes/fib';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const isEqual = (arr1: string[], arr2: string[]) => {
  return JSON.stringify(arr1) === JSON.stringify(arr2);
};

function convertArrToLowerCase(arr: string[]) {
  return arr.map((val) => val.toLowerCase());
}

export function isNumeric(obj: unknown): obj is number {
  return (
    !Array.isArray(obj) &&
    Number(obj) - parseFloat(Number(obj).toString()) + 1 >= 0
  );
}

export class BlankCounter {
  counter = -1;
  next() {
    return ++this.counter;
  }
}

export function deepClone<T>(obj: T): T {
  if (typeof obj !== 'object' || obj === null) {
    return obj; // Return non-object types as is
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => deepClone(item)) as unknown as T; // Clone arrays recursively
  }

  return Object.entries(obj).reduce((acc: any, [key, value]) => {
    acc[key] = deepClone(value); // Clone object properties recursively
    return acc;
  }, {} as T);
}

const getTextFromOption = (
  option?: OptionsType,
  resourceCategory?: ResourceCategoryEnum,
  includeOptionNumber?: boolean,
  index?: number
) => {
  if (!option) {
    return '';
  }
  switch (option.optionType.case) {
    case 'textOption':
      return includeOptionNumber && typeof index === 'number'
        ? numberToColumnTitle(index) + ') ' + option.optionType.value.optionText
        : option.optionType.value.optionText;
    default:
      return (
        <ImageWrapper
          path={getMediaBasePath(
            option.optionType.value?.image?.imageUrl,
            resourceCategory ===
              ResourceCategoryEnum.RESOURCE_CATEGORY_MY_RESOURCES
              ? 'userDataBucket'
              : 'processedMediaBucket'
          )}
          name={option.optionType.value?.image?.imageName || 'image'}
          styles={{
            width: {
              xs: '100%',
              md: '50%',
            },
          }}
        />
      );
  }
};

export function getSolutionText(
  question?: Question,
  resourceCategory?: ResourceCategoryEnum,
  includeOptionNumber?: boolean
) {
  if (!question) {
    return '';
  }
  const questionType = question.questionType;
  const questionContent = question.question;
  const questionValue = questionContent?.model.value;
  if (!questionValue) {
    return '';
  }
  switch (questionType) {
    case Question_QuestionEnum.QUESTION_TYPE_TF:
      if (questionValue instanceof TfContentModel) {
        const correct = questionValue.correct as
          | string
          | TfContentModel_AnswerEnum;
        switch (correct) {
          case TfContentModel_AnswerEnum.TF_ANSWER_F:
          case 'TF_ANSWER_F':
            return 'False';
          case TfContentModel_AnswerEnum.TF_ANSWER_T:
          case 'TF_ANSWER_T':
            return 'True';
          default:
            return '';
        }
      }
      return '';
    case Question_QuestionEnum.QUESTION_TYPE_MCQS:
      if (questionValue instanceof McqSingleContentModel) {
        const options = questionValue.options;
        const correct = questionValue.correct;
        const correctAnswer =
          correct && !isNaN(Number(correct))
            ? getTextFromOption(
                options[Number(correct) - 1],
                resourceCategory,
                includeOptionNumber,
                Number(correct) - 1
              )
            : '';
        return correctAnswer;
      } else {
        return '';
      }
    case Question_QuestionEnum.QUESTION_TYPE_MCQM:
      if (questionValue instanceof McqMultipleContentModel) {
        const options = questionValue.options;
        const correct = questionValue.correct;
        const val = correct
          .map((corr) =>
            corr && !isNaN(Number(corr))
              ? getTextFromOption(
                  options[Number(corr) - 1],
                  resourceCategory,
                  includeOptionNumber,
                  Number(corr) - 1
                )
              : ''
          )
          .join(', ');
        return val;
      } else {
        return '';
      }
    case Question_QuestionEnum.QUESTION_TYPE_FIB:
      if (questionValue instanceof FibContentModel) {
        const correct = questionValue.correctAnswerInfo;
        const arr: string[][] = [];
        correct.forEach((corr) => (arr[corr.blankPosition] = corr.correct));

        // Find the maximum length among the inner arrays
        const maxLength = Math.max(...arr.map((subArr) => subArr.length));

        // Initialize the result array
        const result: string[][] = Array(maxLength)
          .fill(0)
          .map(() => Array(arr.length).fill(''));

        // Fill the result array by switching the indices (transpose)
        for (let i = 0; i < arr.length; i++) {
          for (let j = 0; j < maxLength; j++) {
            result[j][i] = arr[i][j];
          }
        }
        const array = [];
        for (let i = 0; i < result.length; i++) {
          array[i] = result[i].join(', ');
        }
        return '[' + array.join('], [') + ']';
      } else {
        return '';
      }

    case Question_QuestionEnum.QUESTION_TYPE_CBQ:
    case Question_QuestionEnum.QUESTION_TYPE_LONG:
    case Question_QuestionEnum.QUESTION_TYPE_SHORT:
    case Question_QuestionEnum.QUESTION_TYPE_VERY_SHORT:
      if (
        questionValue instanceof LongQuesContentModel ||
        questionValue instanceof ShortQuesContentModel ||
        questionValue instanceof VeryShortQuesContentModel ||
        questionValue instanceof CBQQuesContentModel
      ) {
        const sampleAnswersArray = questionValue.sampleAnswers;
        const superAnswers =
          question.questionMeta?.llmQuestionContextInfo?.superAnswer
            .map((val) => (val.endsWith('.') ? val : val + '.'))
            .join(' ');
        return superAnswers && superAnswers.length
          ? superAnswers
          : sampleAnswersArray
              .map((sample) =>
                sample.answer
                  .map((val) => (val.endsWith('.') ? val : val + '.'))
                  .join(' ')
              )
              .join('\n');
      } else {
        return '';
      }
    default:
      return '';
  }
}

/** Raw `correct` from question model. MCQM returns string[][]; FIB returns string[][]. */
export function getModelCorrectAnswer(
  question?: Question
): string[] | string[][] {
  if (!question) {
    return [];
  }
  const questionValue = question.question?.model?.value;
  if (!questionValue) {
    return [];
  }

  switch (question.questionType) {
    case Question_QuestionEnum.QUESTION_TYPE_MCQS:
      if (questionValue instanceof McqSingleContentModel) {
        return questionValue.correct ? [questionValue.correct] : [];
      }
      return [];
    case Question_QuestionEnum.QUESTION_TYPE_MCQM:
      if (questionValue instanceof McqMultipleContentModel) {
        return questionValue.correct.length ? questionValue.correct : [];
      }
      return [];
    case Question_QuestionEnum.QUESTION_TYPE_TF:
      if (questionValue instanceof TfContentModel) {
        return [questionValue.correct.toString()];
      }
      return [];
    case Question_QuestionEnum.QUESTION_TYPE_FIB:
      if (questionValue instanceof FibContentModel) {
        return [];
      }
      return [];
    default:
      return [];
  }
}

/** Flatten model correct for ContentAttempt.answer / userAttemptedAnswer. */
export function modelCorrectToAnswerArray(
  correct: string[] | string[][]
): string[] {
  if (!correct.length) {
    return [];
  }
  if (Array.isArray(correct[0])) {
    return (correct as string[][])[0] ?? [];
  }
  return correct as string[];
}

export function isCorrectAnswer(
  question?: Question,
  userAttemptedAnswer?: string[],
  isCorrect?: QAttemptResultEnum
) {
  if (!question) {
    return QAttemptResultEnum.RESPONSE_UNDEFINED;
  }
  if (!userAttemptedAnswer) {
    return QAttemptResultEnum.RESPONSE_UNDEFINED;
  }
  const questionType = question.questionType;
  const questionContent = question.question;
  const questionValue = questionContent?.model.value;
  if (!questionValue) {
    return QAttemptResultEnum.RESPONSE_UNDEFINED;
  }
  if (!userAttemptedAnswer.length) {
    return QAttemptResultEnum.RESPONSE_UNDEFINED;
  }
  if (isCorrect) {
    return isCorrect;
  }
  switch (questionType) {
    case Question_QuestionEnum.QUESTION_TYPE_TF:
      if (questionValue instanceof TfContentModel) {
        const correct = questionValue.correct.toString();
        const ans = userAttemptedAnswer[0];
        if (ans === correct) {
          return QAttemptResultEnum.RESPONSE_CORRECT;
        }
        return QAttemptResultEnum.RESPONSE_INCORRECT;
      }
      return QAttemptResultEnum.RESPONSE_UNDEFINED;
    case Question_QuestionEnum.QUESTION_TYPE_MCQS:
      if (questionValue instanceof McqSingleContentModel) {
        const correct = questionValue.correct;
        console.log({ correct, userAttemptedAnswer });
        return correct === userAttemptedAnswer[0]
          ? QAttemptResultEnum.RESPONSE_CORRECT
          : QAttemptResultEnum.RESPONSE_INCORRECT;
      }
      return QAttemptResultEnum.RESPONSE_UNDEFINED;
    case Question_QuestionEnum.QUESTION_TYPE_MCQM:
      if (questionValue instanceof McqMultipleContentModel) {
        const correct = questionValue.correct;
        const completeCorrect = isEqual(
          questionValue.correct.sort(),
          userAttemptedAnswer.sort()
        );

        if (completeCorrect) {
          return QAttemptResultEnum.RESPONSE_CORRECT;
        } else if (userAttemptedAnswer.some((a) => correct.includes(a))) {
          return QAttemptResultEnum.RESPONSE_PARTIALLY_CORRECT;
        } else return QAttemptResultEnum.RESPONSE_INCORRECT;
      }
      return QAttemptResultEnum.RESPONSE_UNDEFINED;
    case Question_QuestionEnum.QUESTION_TYPE_FIB:
      if (questionValue instanceof FibContentModel) {
        const fibQues = new Fib(question);
        return fibQues.isCorrect(userAttemptedAnswer);
      } else return QAttemptResultEnum.RESPONSE_UNDEFINED;

    case Question_QuestionEnum.QUESTION_TYPE_VERY_SHORT:
    case Question_QuestionEnum.QUESTION_TYPE_SHORT:
    case Question_QuestionEnum.QUESTION_TYPE_CBQ:
    case Question_QuestionEnum.QUESTION_TYPE_LONG:
      if (
        questionValue instanceof VeryShortQuesContentModel ||
        questionValue instanceof ShortQuesContentModel ||
        questionValue instanceof LongQuesContentModel ||
        questionValue instanceof CBQQuesContentModel
      ) {
        return isCorrect ? isCorrect : QAttemptResultEnum.RESPONSE_UNDEFINED;
      } else return QAttemptResultEnum.RESPONSE_UNDEFINED;

    default:
      return QAttemptResultEnum.RESPONSE_UNDEFINED;
  }
}

function areSetsEqual(array1: string[], array2: string[]): boolean {
  const set1 = new Set(array1.map((item) => item.toLowerCase()));
  const set2 = new Set(array2.map((item) => item.toLowerCase()));

  if (set1.size !== set2.size) {
    return false;
  }
  for (const item of set1) {
    if (!set2.has(item)) {
      return false;
    }
  }
  return true;
}

function does2DArrayContainArray(
  twoDArray: string[][],
  singleArray: string[]
): boolean {
  for (const innerArray of twoDArray) {
    if (areArraysEqual(innerArray, singleArray)) {
      return true;
    }
  }

  return false;
}

function areArraysEqual(array1: string[], array2: string[]): boolean {
  if (array1.length !== array2.length) {
    return false;
  }

  for (let i = 0; i < array1.length; i++) {
    if (array1[i].toLowerCase() !== array2[i].toLowerCase()) {
      return false;
    }
  }

  return true;
}

export function numberToColumnTitle(n: number) {
  let result = '';
  n++; // Convert 0-based to 1-based indexing
  while (n > 0) {
    n--; // Adjust for 0-based character (A = 0)
    result = String.fromCharCode((n % 26) + 65) + result;
    n = Math.floor(n / 26);
  }
  return result;
}

export const toStringBloomLevel = (value?: string | BloomLevelEnum): string => {
  if (!value) {
    return '';
  }
  switch (value) {
    case 'remember':
    case BloomLevelEnum.BLOOMLEVEL_RETENTION:
      return 'Retention';
    case 'analyze':
    case BloomLevelEnum.BLOOMLEVEL_ANALYSIS:
      return 'Analysis';
    case 'apply':
    case BloomLevelEnum.BLOOMLEVEL_APPLICATION:
      return 'Application';
    case 'understand':
    case BloomLevelEnum.BLOOMLEVEL_COMPREHENSION:
      return 'Comprehension';
    case 'create':
    case BloomLevelEnum.BLOOMLEVEL_CREATION:
      return 'Creation';
    case 'evaluate':
    case BloomLevelEnum.BLOOMLEVEL_EVALUATION:
      return 'Evaluation';
    default:
      return '';
  }
};

type UseTimerParams = {
  initialTime: number;
  startInitially?: boolean;
  incrementPerSecond?: number;
  delay?: number; // in ms
  /** When true, the counter stops incrementing until false (does not reset value). */
  isPaused?: boolean;
};

type UseTimerReturn = {
  value: number;
  start: () => void;
  end: () => void;
  reset: () => void;
  isRunning?: boolean;
};

export function useSecondsTimer({
  initialTime,
  startInitially = false,
  incrementPerSecond = 1,
  delay = 0,
  isPaused = false,
}: UseTimerParams): UseTimerReturn {
  const [value, setValue] = useState(initialTime);
  const [isRunning, setIsRunning] = useState(startInitially);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    clearTimers();

    if (delay > 0) {
      timeoutRef.current = setTimeout(() => {
        setIsRunning(true);
      }, delay);
    } else {
      setIsRunning(true);
    }
  }, [clearTimers, delay]);

  const end = useCallback(() => {
    clearTimers();
    setIsRunning(false);
  }, [clearTimers]);

  const reset = useCallback(() => {
    clearTimers();
    setIsRunning(false);
    setValue(initialTime);
  }, [clearTimers, initialTime]);

  useEffect(() => {
    if (!isRunning || isPaused) return;

    intervalRef.current = setInterval(() => {
      setValue((prev) => prev + incrementPerSecond);
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isRunning, incrementPerSecond, isPaused]);

  useEffect(() => {
    if (startInitially) {
      if (delay > 0) {
        timeoutRef.current = setTimeout(() => {
          setIsRunning(true);
        }, delay);
      } else {
        setIsRunning(true);
      }
    }

    return () => {
      clearTimers();
    };
  }, [startInitially, delay, clearTimers]);

  useEffect(() => {
    setValue(initialTime);
  }, [initialTime]);

  return {
    value,
    start,
    end,
    reset,
    isRunning,
  };
}

/**
 * Countdown: starts at `loadingTime` and decrements once per second until 0.
 * Returns the current remaining seconds, or `0` when finished. The underlying timer is stopped via
 * {@link useSecondsTimer.end} when the value reaches 0 — do not stop it by updating state during render.
 *
 * @param resetKey When set, the countdown restarts from `loadingTime` whenever this value changes (e.g. question id).
 */
export function useLoadingTimer(
  loadingTime = 2,
  resetKey?: string | number | null
) {
  const loadingTimer = useSecondsTimer({
    initialTime: loadingTime,
    startInitially: true,
    incrementPerSecond: -1,
  });

  useEffect(() => {
    if (resetKey === undefined) return;
    loadingTimer.reset();
    loadingTimer.start();
  }, [resetKey, loadingTimer.reset, loadingTimer.start]);

  useEffect(() => {
    if (loadingTimer.value <= 0 && loadingTimer.isRunning) {
      loadingTimer.end();
    }
  }, [loadingTimer.value, loadingTimer.isRunning, loadingTimer.end]);

  return useMemo(
    () => (loadingTimer.value > 0 ? loadingTimer.value : 0),
    [loadingTimer.value]
  );
}
